import * as React from 'react';
import { ReactElement } from 'react';
import styled from 'styled-components';

import { flexAllCenter } from 'styles/mixins';
import { COLORS, hexWithOpacity } from 'styles/style-constants';

import BlankButton from './BlankButton';

const ButtonBody = styled(BlankButton)<{
    $isBig?: boolean;
    $pending?: boolean;
    $fullWidth?: boolean;
    $secondary?: boolean;
    $isSquare?: boolean;
    $isSmall?: boolean;
    $isWhite?: boolean;
    $isPurpleText?: boolean;
    $isRounded?: boolean;
    $tertiary?: boolean;
    $withGradient?: boolean;
}>`
    ${flexAllCenter};
    width: ${({ $fullWidth, $isSquare, $isBig }) => {
        if ($fullWidth) {
            return '100%';
        }
        if ($isSquare) {
            return $isBig ? '6.6rem' : '4.8rem';
        }

        return 'unset';
    }};
    height: ${({ $isBig, $isSmall }) => {
        if ($isBig) {
            return '6.6rem';
        }

        if ($isSmall) {
            return '2.8rem';
        }

        return '4.8rem';
    }};
    padding: ${({ $isBig, $isSquare, $isSmall }) => {
        if ($isSquare) {
            return 'unset';
        }

        if ($isSmall) {
            return '0 1.8rem';
        }

        return $isBig ? '0 6.4rem' : '0 3.2rem';
    }};
    background-color: ${COLORS.surface};
    background: ${({ $secondary, $isWhite, $tertiary, $withGradient }) => {
        if ($secondary && $withGradient) {
            return `radial-gradient(100% 158.34% at 0% 0%, ${hexWithOpacity(COLORS.purpleGlow, 20)} 0%, ${hexWithOpacity(COLORS.purpleGlow, 0)} 100%)`;
        }
        if ($secondary) {
            return COLORS.secondaryButtonBackground;
        }
        if ($isWhite) {
            return COLORS.surface;
        }
        if ($tertiary) {
            return COLORS.controlBackground;
        }
        if ($withGradient) {
            return `radial-gradient(146.92% 150% at 50.22% 0%, ${COLORS.gradientStart} 0%, ${COLORS.gradientEnd} 100%)`;
        }

        return COLORS.primaryButton;
    }};

    box-shadow: ${({ $withGradient, $secondary, $tertiary }) =>
        $withGradient && !$secondary && !$tertiary
            ? `inset 0 0 0 4px ${hexWithOpacity(COLORS.white, 8)}, 0 4px 10px ${hexWithOpacity(COLORS.black, 20)}`
            : 'unset'};
    
    border: ${({ $withGradient, $secondary, $tertiary, $isWhite }) => {
        if ($withGradient && $secondary) {
            return `0.1rem solid ${COLORS.purple100};`;
        }
        if ($secondary || $tertiary || $isWhite) {
            return `0.1rem solid ${COLORS.controlBorder};`;
        }

        return `0.1rem solid ${COLORS.transparent};`;
    }}

    border-radius: ${({ $isRounded, $isBig, $isSmall }) => {
        if (!$isRounded) {
            return '0.5rem';
        }
        if ($isBig) {
            return '2.4rem';
        }
        if ($isSmall) {
            return '0.8rem';
        }
        return '1.6rem';
    }};
    font-weight: bold;
    letter-spacing: ${({ $isBig }) => ($isBig ? '0.2rem' : '0.05rem')};
    text-transform: uppercase;
    //TODO: remove
    cursor: pointer;
    transition: all ease 200ms;
    position: relative;
    pointer-events: ${({ $pending }) => ($pending ? 'none' : 'auto')};
    white-space: nowrap;

    &:hover {
        background: ${({ $secondary, $isWhite, $tertiary, $withGradient }) => {
            if ($secondary && $withGradient) {
                return `radial-gradient(100% 158.34% at 0% 0%, ${hexWithOpacity(COLORS.purpleGlow, 30)} 0%, ${hexWithOpacity(COLORS.purpleGlow, 0)} 100%)`;
            }

            if ($secondary) {
                return COLORS.secondaryButtonHover;
            }

            if ($isWhite) {
                return COLORS.controlHover;
            }

            if ($tertiary) {
                return COLORS.controlHover;
            }

            if ($withGradient) {
                return `radial-gradient(146.92% 150% at 50.22% 0%, ${COLORS.gradientHover} 0%, ${COLORS.gradientBlueHover} 100%)`;
            }

            return COLORS.primaryButtonHover;
        }};
    }

    &:active {
        transform: scale(0.9);
    }

    &:focus {
        border-width: 0.1rem;
        border-color: ${({ $withGradient, $secondary, $tertiary, $isWhite }) => {
            if ($withGradient && $secondary) return COLORS.purple100;
            return $secondary || $tertiary || $isWhite ? COLORS.controlBorder : COLORS.transparent;
        }};
    }

    ${({ $secondary, $withGradient }) =>
        $secondary &&
        $withGradient &&
        `&:hover:not(:disabled) {
            border-color: ${COLORS.secondaryGradientHoverBorder};
        }`}

    &:disabled {
        background: ${({ $withGradient }) => {
            if ($withGradient) {
                return `radial-gradient(146.92% 150% at 50.22% 0%, ${COLORS.gray100} 0%, ${COLORS.gray200} 100%)`;
            }

            return COLORS.disabledButtonBackground;
        }};
        cursor: not-allowed;
    }

    &:active,
    &:disabled {
        transform: none;
    }
`;

const ButtonLoader = styled.div<{
    $pending?: boolean;
    $secondary?: boolean;
    $isWhite?: boolean;
    $isPurpleText?: boolean;
    $tertiary?: boolean;
    $withGradient?: boolean;
}>`
    color: ${({ $secondary, $isWhite, $isPurpleText, $tertiary, $withGradient }) => {
        if ($isPurpleText) {
            return COLORS.purple500;
        }

        if ($secondary && $withGradient) {
            return COLORS.textPrimary;
        }

        if ($secondary) {
            return COLORS.secondaryButtonText;
        }

        if ($isWhite) {
            return COLORS.blue500;
        }

        if ($tertiary) {
            return COLORS.textPrimary;
        }

        return COLORS.white;
    }};

    button:disabled > & {
        color: ${COLORS.disabledButtonText};
    }

    ${({ $pending }) =>
        $pending
            ? `
        background: linear-gradient(
            110deg,
            ${COLORS.white},
            ${COLORS.white} 33%,
            ${COLORS.loaderHighlight} 33%,
            ${COLORS.loaderHighlight} 66%,
            ${COLORS.white} 66%,
            ${COLORS.white} 100%
        );
        color: transparent;
        -webkit-background-clip: text;
        background-clip: text;
        animation: rainbow_animation 1s ease-in infinite;
        background-size: 400% 100%;
        pointer-events: none;
    `
            : `
        ${flexAllCenter};
    `};

    @keyframes rainbow_animation {
        0% {
            background-position: 100% 0;
        }

        100% {
            background-position: 0 0;
        }
    }
`;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode;
    pending?: boolean;
    isBig?: boolean;
    fullWidth?: boolean;
    secondary?: boolean;
    tertiary?: boolean;
    isSquare?: boolean;
    isSmall?: boolean;
    isWhite?: boolean;
    isPurpleText?: boolean;
    isRounded?: boolean;
    withGradient?: boolean;
}

const Button = ({
    children,
    pending,
    isBig,
    isSmall,
    fullWidth,
    secondary,
    tertiary,
    isSquare,
    isWhite,
    isPurpleText,
    isRounded,
    withGradient,
    ...props
}: ButtonProps): ReactElement => (
    <ButtonBody
        $pending={pending}
        $isBig={isBig}
        $fullWidth={fullWidth}
        $secondary={secondary}
        $isSquare={isSquare}
        $isSmall={isSmall}
        $isWhite={isWhite}
        $isRounded={isRounded}
        $tertiary={tertiary}
        $withGradient={withGradient}
        {...props}
    >
        <ButtonLoader
            $pending={pending}
            $secondary={secondary}
            $tertiary={tertiary}
            $isWhite={isWhite}
            $isPurpleText={isPurpleText}
            $withGradient={withGradient}
        >
            {children}
        </ButtonLoader>
    </ButtonBody>
);

export default Button;
